import { useAuth } from "../../auth/AuthContext";
import { logout as logoutRequest } from "../../lib/auth";
import { queryClient } from "../../lib/queryClient";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

/**
 * The Account dialog from the canvas: who you are signed in as, the role and
 * tenant that decides what you may administer, and how the session was
 * established. Read-only — nothing here is editable, because none of it is set
 * in this product.
 */
export function AccountDialog({ onClose }: { onClose: () => void }): JSX.Element | null {
  const { user } = useAuth();
  if (!user) return null;

  const tenant = user.email.includes("@") ? user.email.split("@")[1] : "—";

  async function onLogout(): Promise<void> {
    await logoutRequest();
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    window.location.assign("/login");
  }

  return (
    <div className="dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="dialog dialog-sm"
        role="dialog"
        aria-modal="true"
        aria-label="Account"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dialog-body account-body">
          <div className="account-identity">
            <span className="account-avatar" data-on-brand>{initials(user.displayName)}</span>
            <div>
              <strong>{user.displayName}</strong>
              <span className="mono">{user.email}</span>
            </div>
          </div>
          <div className="account-facts">
            <span>
              Role · <strong>{user.roles[0] ?? "User"}</strong>
            </span>
            <span>
              Tenant · <strong>{tenant}</strong>
            </span>
            <span>
              Session ·{" "}
              {user.provider === "development"
                ? "development authentication — Microsoft Entra SSO is not configured"
                : "signed in with Microsoft 365 SSO"}
            </span>
          </div>
        </div>
        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void onLogout()}>
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}
